/**
 * The last touch of a visitor: the campaign parameters and the ad click ids of the URL that
 * brought the visitor, the landing page and the outside referrer. music24-api stores it on the
 * user at the signup, so we know which ad or link gave the signup.
 *
 * It lives in first-party `localStorage` under one key. music24.com and the web app at
 * music24.com/app share the origin, so a visitor who lands on the website and signs up in the web
 * app keeps the touch. The website has a twin of this file
 * (`music24-website/app/src/components/attribution/attributionStore.ts`): keep the two files
 * equal.
 *
 * Rules (the Quizado model):
 * - The last touch that is not direct wins. A URL with a campaign parameter or a click id is a
 *   touch, and so is an arrival from another site. A direct visit and a move inside our own site
 *   keep the stored touch. Two touches never mix.
 * - A touch is valid for 90 days.
 * - Nothing here goes to a third party. The web app sends the click ids, the referrer and the
 *   landing page to music24-api only after Accept in the consent bar (`signupAttribution.ts`).
 */

export const ATTRIBUTION_KEY = 'm24Attribution';

export const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'] as const;
export const CLICK_ID_PARAMS = ['gclid', 'wbraid', 'gbraid', 'fbclid', 'msclkid'] as const;
export const ATTRIBUTION_PARAMS: readonly string[] = [...CAMPAIGN_PARAMS, ...CLICK_ID_PARAMS];

const TTL_SECONDS = 90 * 24 * 3600;
const MAX_VALUE_LENGTH = 512;
const MAX_URL_LENGTH = 1024;

export interface StoredAttribution {
  /** Parameter name to value, as the URL had them. */
  v: Record<string, string>;
  /** The time of the touch, in seconds. */
  t: number;
  /** The landing page of the touch. */
  lp?: string;
  /** The outside referrer of the touch. */
  r?: string;
}

/**
 * Referrers that are a step of a visit we already know, not a new touch: the visitor reads our
 * email, signs in with Google or Apple, comes back from Stripe Checkout or passes the staging
 * login. Such a referrer must not replace an ad click right before the signup.
 */
const NON_TOUCH_REFERRER_HOSTS = [
  'mail.google.com',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.yahoo.com',
  'mail.proton.me',
  'checkout.stripe.com',
  'billing.stripe.com',
  'pay.stripe.com',
  'accounts.google.com',
  'appleid.apple.com',
];
const NON_TOUCH_REFERRER_SUFFIXES = ['.cloudflareaccess.com'];

const nowSeconds = (): number => Math.floor(Date.now() / 1000);

const readStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : (window.localStorage ?? null);
  } catch {
    return null;
  }
};

/** The stored touch from its JSON text. Anything of the wrong shape or older than 90 days is none. */
export const parseStoredAttribution = (raw: string | null | undefined): StoredAttribution | undefined => {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredAttribution> | null;
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      parsed.v === null ||
      typeof parsed.v !== 'object' ||
      Array.isArray(parsed.v) ||
      typeof parsed.t !== 'number' ||
      !Number.isFinite(parsed.t) ||
      Object.values(parsed.v).some((value) => typeof value !== 'string')
    ) {
      return undefined;
    }
    const age = nowSeconds() - parsed.t;
    if (age < 0 || age > TTL_SECONDS) {
      return undefined;
    }
    return {
      v: parsed.v,
      t: parsed.t,
      lp: typeof parsed.lp === 'string' ? parsed.lp : undefined,
      r: typeof parsed.r === 'string' ? parsed.r : undefined,
    };
  } catch {
    return undefined;
  }
};

export const readStoredAttribution = (): StoredAttribution | undefined => {
  try {
    return parseStoredAttribution(readStorage()?.getItem(ATTRIBUTION_KEY));
  } catch {
    return undefined;
  }
};

const writeStoredAttribution = (touch: StoredAttribution): void => {
  try {
    readStorage()?.setItem(ATTRIBUTION_KEY, JSON.stringify(touch));
  } catch {
    // A browser that blocks storage keeps no touch. The signup then has no source.
  }
};

const readUrlParams = (href: string): Record<string, string> => {
  const found: Record<string, string> = {};
  try {
    const params = new URL(href).searchParams;
    ATTRIBUTION_PARAMS.forEach((key) => {
      const value = params.get(key)?.trim();
      if (value !== undefined && value !== '') {
        found[key] = value.substring(0, MAX_VALUE_LENGTH);
      }
    });
  } catch {
    // Not a URL: no parameters.
  }
  return found;
};

/** True for a referrer on another site that is a real arrival (see `NON_TOUCH_REFERRER_HOSTS`). */
export const isExternalReferrer = (referrer: string, ownHost: string): boolean => {
  if (referrer === '') {
    return false;
  }
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return (
      host !== ownHost.toLowerCase() &&
      !NON_TOUCH_REFERRER_HOSTS.includes(host) &&
      !NON_TOUCH_REFERRER_SUFFIXES.some((suffix) => host.endsWith(suffix))
    );
  } catch {
    return false;
  }
};

const sameValues = (a: Record<string, string>, b: Record<string, string>): boolean =>
  Object.keys(a).length === Object.keys(b).length && Object.entries(a).every(([key, value]) => b[key] === value);

// The referrer of a document describes its first load. One capture for each document, so a move
// inside the single-page app never becomes a new touch.
let capturedDocument: Document | null = null;

/** Reads the touch of this page load and stores it. Call it once, when the page starts. */
export const captureAttribution = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || capturedDocument === document) {
    return;
  }
  capturedDocument = document;
  const { href } = window.location;
  const fromUrl = readUrlParams(href);
  const referrer = isExternalReferrer(document.referrer, window.location.hostname) ? document.referrer : undefined;
  const hasParams = Object.keys(fromUrl).length > 0;
  if (!hasParams && referrer === undefined) {
    return;
  }
  // A reload, or the step from the website to the web app with the same parameters, is the same
  // touch: keep its first landing page and its time.
  const previous = readStoredAttribution();
  if (hasParams && referrer === undefined && previous !== undefined && sameValues(previous.v, fromUrl)) {
    return;
  }
  writeStoredAttribution({
    v: fromUrl,
    t: nowSeconds(),
    lp: href.substring(0, MAX_URL_LENGTH),
    r: referrer?.substring(0, MAX_URL_LENGTH),
  });
};

/** For tests: forget that this document was captured. */
export const resetAttributionCapture = (): void => {
  capturedDocument = null;
};
